import type { Styles } from '@adornis/ass/style.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { type ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './tr-icon.js';

@customElement('tr-button-small')
export class TRButtonSmall extends ChemistryLitElement {
  @property({ attribute: 'icon' }) icon!: string;
  @property() action!: (e: MouseEvent) => any;
  @property({ attribute: false }) color?: ColorType;
  @property({ attribute: 'mode' }) mode?: 'outline' | 'fill';
  @property({ attribute: 'not-clickable', type: Boolean }) notClickable = false;

  get bgColor() {
    return this.color ?? this.colors.accent;
  }

  get _mode() {
    return this.mode ?? 'fill';
  }

  override render() {
    return html`
      <tr-flex
        class="container"
        padding="xs"
        horizontal
        space="sm"
        crossaxis-center
        center
        @click=${e => this.notClickable || this.action?.(e)}
        h100
      >
        ${this.icon ? html` <tr-icon> ${this.icon} </tr-icon> ` : nothing}
        <slot></slot>
      </tr-flex>
    `;
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        '.container': {
          ...(this._mode === 'fill'
            ? { background: this.bgColor, color: '#fff' }
            : { border: `1px solid ${this.bgColor}`, color: this.bgColor }),
          borderRadius: this.sizes.borderRadius,
          opacity: this.notClickable ? 0.3 : 1,
        },
        '.container:hover': this.notClickable
          ? {
              userSelect: 'none',
              cursor: 'not-allowed',
            }
          : {
              background: this.bgColor.shade(300),
              ...(this._mode === 'outline' ? { color: '#fff' } : {}),
              cursor: 'pointer',
            },
      },
    ];
  }
}

export const trButton = ({
  icon,
  action,
  actionRaw,
  color,
  mode,
  notClickable,
  text,
  tr,
  tooltip,
  tooltipTr,
}: {
  icon?: string;
  action?: (e: MouseEvent) => any;
  actionRaw?: (e: MouseEvent) => any;
  color?: ColorType;
  mode?: 'outline' | 'fill';
  notClickable?: boolean;
  text?: string;
  tr?: string[];
  tooltip?: string | null;
  tooltipTr?: string[];
}) => {
  if (action && actionRaw) throw new Error('Cannot provide both action and actionRaw');
  const _action =
    actionRaw ??
    (async e => {
      if (!action) return;
      try {
        const m = await import('@adornis/dialog/x-dialog.js');
        const maybePromise = action(e);
        if (maybePromise instanceof Promise) {
          await m.XDialog.waitFor(maybePromise);
        }
      } catch (e) {
        const err = e as any;
        console.error(err);
        const translate = clientTranslator.translate;
        const errMessage = (() => {
          if (err?.extensions?.translationKey) {
            return translate('errors', err.extensions.translationKey);
          }

          const errCode = err?.message ?? 'unknown';
          return translate('err', errCode);
        })();

        return XSnackbar.show(errMessage);
      }
    });

  if (tr) {
    text = (clientTranslator.translate as (...args: string[]) => string)(...tr);
  }

  return html`
    <tr-button-small .icon=${icon} .action=${_action} .color=${color} .mode=${mode} .notClickable=${notClickable}
      >${text ?? nothing}
      ${tooltip || tooltipTr
        ? html`<x-tooltip
            .text=${tooltip || html`<x-text>${(clientTranslator.translate as (...args: string[]) => string)(...tooltipTr!)}</x-text>`}
          >
          </x-tooltip>`
        : nothing}
    </tr-button-small>
  `;
};
